import * as React from 'react';

import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Switch, Route, Redirect, useRouteMatch } from 'react-router-dom';

import AppState from '@webapp/store/state';

import TabsNav, { TabProp } from '@components/tabs-nav';
import FormContainer from '@components/layout/form-container';
import MotionOpacity from '@components/motion/motion-opacity';
import SmallNotification from '@components/small-notification';
import SidebarWrapper from '@components/layout/sidebar-wrapper';
import SidebarContainer from '@components/layout/sidebar-container';

import { Translate } from 'react-localize-redux';
import { push } from 'connected-react-router';
import styled from 'styled-components';
import LabelGeneralPage from './label-general-page';
import LabelRestoreSuccess from './label-restore-success';
import LabelDeleteSuccess from './label-delete-success';
import LabelRestorePage from './label-restore-page';
import LabelDeletePage from './label-delete-page';
import LabelProfileSidebarCtrl, {
  labelProfileMenuSections,
} from '../../controllers/label/label-sidebar';
import RosterManagementPage from './roster-management-page';
import {LabelTeamPage} from './label-team-page';
import { LabelDealsPage } from './label-deals-page';

type LabelProfileTabs = 'general' | 'team' | 'roster';

interface ProfilePageProps {
  className?: string;
  isMobile?: boolean;
}

const mapStateToProps = (state: AppState) => {
  return {
    isMobile: state.ui.mobile,
  };
};

const initialTab = 0;

export const LabelProfilePage: React.FC<ProfilePageProps> = (props) => {
  const match = useRouteMatch<{ account: string }>();
  const location = useLocation();

  const toastNotification = useSelector(
    (state: AppState) => state.ui.toastNotification
  );

  const isOwner = useSelector(
    (state: AppState) =>
      state.user.profile?.id === state.recordLabels.active?.ownerId
  );

  const dealsEnabled = useSelector((state: AppState) => !!state.recordLabels.active?.flags?.deals);

  const showSidebar =
    !location.pathname.includes('/delete/') &&
    !location.pathname.endsWith('/restore/');

    const tabsData: TabProp<LabelProfileTabs>[] = React.useMemo(() => labelProfileMenuSections({ deals: dealsEnabled }).map((section) => ({
      label: section.label,
      key: section.path.split('/')[2] as LabelProfileTabs,
    })), [dealsEnabled])

  const [activeTab, setActiveTab] = React.useState<LabelProfileTabs>(
    tabsData[initialTab].key
  );

  const getActiveTab = (url: string): LabelProfileTabs => {
    if (url.endsWith('general')) {
      return 'general';
    } else if (url.endsWith('team')) {
      return 'team';
    } else if (url.endsWith('roster')) {
      return 'roster';
    }
    return 'general';
  };

  React.useEffect(() => {
    setActiveTab(getActiveTab(location.pathname));
  }, [location.pathname]);

  const dispatch = useDispatch();

  const handleTabClick = (value: LabelProfileTabs) => {
    setActiveTab(value);
    dispatch(push(`/user/${value}`));
  };

  if (!isOwner) {
    return null;
  }

  return (
    <MotionOpacity className={props.className}>
      <SidebarContainer className="no-margin-inline">
        {showSidebar && (
          <SidebarWrapper>
            <LabelProfileSidebarCtrl />
          </SidebarWrapper>
        )}
        {showSidebar && props.isMobile && (
          <TabsNav
            className="tab-nav"
            tabs={tabsData}
            currentTab={tabsData.findIndex((tab) => tab.key === activeTab)}
            onChange={(tab) => handleTabClick(tab.key as LabelProfileTabs)}
            bigFont
            activeBigFont
            overflow
          />
        )}
        <FormContainer hasSidebar={showSidebar} noMinHeight>
          <Switch>
            <Route
              exact
              path={`${match.path}/general`}
              render={() => <LabelGeneralPage />}
            />
            <Route
              exact
              path={`${match.path}/team`}
              render={() => <LabelTeamPage />}
            />
            <Route
              exact
              path={`${match.path}/roster`}
              render={() => <RosterManagementPage />}
            />
            <Route
              exact
              path={`${match.path}/deals`}
              render={() => <LabelDealsPage />}
            />
            <Route
              exact
              path={`${match.path}/restore/success`}
              render={() => <LabelRestoreSuccess />}
            />
            <Route
              exact
              path={`${match.path}/delete/success`}
              render={() => <LabelDeleteSuccess />}
            />
            <Route
              exact
              path={`${match.path}/restore/:labelId`}
              render={() => <LabelRestorePage />}
            />
            <Route
              exact
              path={`${match.path}/delete/:labelId`}
              render={() => <LabelDeletePage />}
            />
            <Route
              exact
              path={match.path}
              render={() => <Redirect to={`${match.path}/general`} />}
            />
          </Switch>
        </FormContainer>
        {toastNotification?.value === 'label_changes_saved' && (
          <SmallNotification
            notificationType={toastNotification.type}
            title={<Translate id={toastNotification.label} />}
          />
        )}
      </SidebarContainer>
    </MotionOpacity>
  );
};
//
export default connect(mapStateToProps)(styled(LabelProfilePage)`
  .tab-nav {
    padding-inline: 24px;
  }
`);
